'use client';

import { frontendApi } from "@/lib/api-requests";
import { NATIVE_LANGUAGES } from "@/lib/constants";
import { appTutorialService } from "@/lib/services/TutorialService";
import { tutorialActions, TutorialMessage } from "@/store/reducers/tutorialSlice";
import { RootState } from "@/store/store";
import { Image } from "@heroui/image";
import clsx from "clsx";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { useSfxProvider } from "@/hooks/useSfxProvider";

export const TutorialAssistant = () => {
    const tutorialService = appTutorialService;

    const currentUser = useSelector((state: RootState) => state.app.currentUser);

    const messageQueue = useSelector((state: RootState) => state.tutorial.messageQueue);
    const currentMessage = useSelector((state: RootState) => state.tutorial.currentMessage);
    const isPlaying = useSelector((state: RootState) => state.tutorial.isPlaying);
    const isVisible = useSelector((state: RootState) => state.tutorial.isVisible);
    const dismissedMessages = useSelector((state: RootState) => state.tutorial.dismissedMessages);    
    const speechRecordingInProgress = useSelector((state: RootState) => state.tutorial.speechRecordingInProgress);
    const [seenFragments, setSeenFragments] = useState<string[]>([]);
    const preparingMessageAudioLoad = useRef<boolean>(false);
    const [currentMessageText, setCurrentMessageText] = useState<string | null>(null);
    const [metricsLoaded, setMetricsLoaded] = useState<boolean>(false);

    const pathname = usePathname();

    const dispatch = useDispatch();

    const sfxProvider = useSfxProvider();

    const store = useStore<RootState>();

    const playMessage = useCallback(async (message: TutorialMessage) => {
        if (message.fragmentId) {            
            dispatch(tutorialActions.setIsVisible(true)); // temporarily show all the time

            const speechUrl = await frontendApi.assistiveSpeech.getAssistiveSpeechUrl(message.fragmentId, message.language);
            
            if (speechUrl && speechUrl.url) {                
                setCurrentMessageText(speechUrl.text);

                sfxProvider.audioMixer.playSpeech(speechUrl.url, null, () => {
                    console.log('audioMixer:onEnded');
                    // onEnded
                    // Wait for the video to finish playing with a slight delay 
                    preparingMessageAudioLoad.current = false;

                    setTimeout(() => {
                        tutorialService.markFragmentAsSeen(message.fragmentId, message.language).then(_ => {});
                        tutorialService.dismiss(message);
                    }, 500);
                }, () => {
                    // onPlay
                    preparingMessageAudioLoad.current = false;
                        dispatch(tutorialActions.setIsPlaying(true));
                }, () => {
                    // onLoadError
                    preparingMessageAudioLoad.current = false;

                        setTimeout(() => {
                            tutorialService.dismiss(message);
                        }, 500);
                });
            } else {
                preparingMessageAudioLoad.current = false;
                console.warn('No assistive speech URL found for fragment', message.fragmentId, message.language);
                tutorialService.dismiss(message);
            }
        }
    }, [dispatch]);

    useEffect(() => {
        if (currentUser?._id) {
            console.log('TutorialAssistant:init');
            tutorialService.init(store);        
            tutorialService.loadData().then(_ => setMetricsLoaded(true));
            dispatch(tutorialActions.setIsVisible(true)); // temporarily show all the time        
        }
    }, [currentUser?._id]);

    // Handle the messages queue in idle state
    useEffect(() => {
        // Wait for the metrics to be loaded to correctly ignore
        // the messages that have already been seen
        if (!metricsLoaded) {
            return;
        }

        if (currentMessage == null && messageQueue.length > 0 && !isPlaying && !preparingMessageAudioLoad.current) {
            // This will set the current message and remove it from the queue
            dispatch(tutorialActions.takeNextMessage());
        }
    }, [messageQueue, isPlaying, currentMessage, metricsLoaded]);

    useEffect(() => {
        // Remove current and previous messages if that aren't related to current path context
        // (for when the user navigates to a different section of the app)
        if (currentMessage && currentMessage.contextPath && !appTutorialService.messageContextMatches(currentMessage, pathname)) {
            tutorialService.dismiss(currentMessage);
        } else if (currentMessage && currentMessage.modalId) {
            tutorialService.dismiss(currentMessage);

            // Also clear queue from all messages with the same modal id
            tutorialService.clearModalIdFragmentFromQueue(currentMessage.modalId);
        }
    }, [pathname]);

    useEffect(() => {
        if (currentMessage && !isPlaying && !preparingMessageAudioLoad.current) {
            // Skip fragments that have already been seen
            if (tutorialService.isFragmentSeen(currentMessage.fragmentId)) {
                tutorialService.dismiss(currentMessage);
                setCurrentMessageText(null);
                dispatch(tutorialActions.setIsPlaying(false));
                return;
            }

            // Ensure that the current path is allowed for the message
            if (currentMessage.contextPath && !appTutorialService.messageContextMatches(currentMessage, pathname)) {
                tutorialService.dismiss(currentMessage);
                setCurrentMessageText(null);
                dispatch(tutorialActions.setIsPlaying(false));
                return;
            }

            preparingMessageAudioLoad.current = true;

            playMessage(currentMessage).then(_ => {});            
        } else if (currentMessage == null && isPlaying) {
            // Stop playing if there are no more messages            
            dispatch(tutorialActions.setIsPlaying(false));      
            setCurrentMessageText(null);      

            sfxProvider.audioMixer.stopSpeech();
        }
    }, [currentMessage, isPlaying, pathname]);

    useEffect(() => {
        // If speech recording is started and we're playing a tutorial message, dismiss it.
        // No need to stop the audio since the speech playback engine will stop it automatically.
        if (speechRecordingInProgress && isPlaying && currentMessage) {
            tutorialService.dismiss(currentMessage);
            setCurrentMessageText(null);
            dispatch(tutorialActions.setIsPlaying(false));
        }
    }, [speechRecordingInProgress, isPlaying, currentMessage]);

    const needsBottomOffset = useMemo(() => {
        if (pathname.includes('/learning-feed') || pathname.includes('/vocab/cards-game')  || pathname.includes('/chat/')) {
            return true;
        }
        return false;
    }, [pathname]);    

    return (
        <div className={clsx("fixed w-full left-0 right-0 z-[55]", needsBottomOffset ? "tutorial-assistant-adaptive-padding-lg sm:bottom-32" : "tutorial-assistant-adaptive-padding sm:bottom-8", isVisible ? "visible" : "hidden")}>
            <div className="sm:max-w-sm md:max-w-md mx-auto flex flex-row items-center gap-2 px-5">
                {/* Avatar image */}
                <div className={"w-16"}><AnimatePresence mode="wait">{isPlaying && <motion.div                     
                    className={"w-16"}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                >
                    <video                    
                        className="rounded-full w-16 border-2 border-white shadow-md grow"                    
                        autoPlay
                        loop
                        muted
                        playsInline
                    >
                        <source src="/images/avatar_chatlanga_talking_1.mp4" type="video/mp4" />
                        <source src="/images/avatar_chatlanga_talking_1.mov" type="video/mov" />
                    </video>
                </motion.div>}</AnimatePresence></div>

                {/* Message text (slightly overlapping behind the avatar) */}
                <div onClick={async () => { 
                    await tutorialService.markFragmentAsSeen(currentMessage.fragmentId, currentMessage.language).then(_ => {});
                    tutorialService.dismiss(currentMessage)
                }}>
                    <AnimatePresence mode="wait">{isPlaying && currentMessageText && 
                        <motion.div className={clsx("bg-white p-4 rounded-xl text-gray-800 z-20 text-xs shadow-md min-h-16")}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.4, ease: "easeInOut" }}
                        >
                        {currentMessageText}
                        {/* A triangle tip for the speech bubble, point right, verticaly centered */}
                    </motion.div>}</AnimatePresence>

                    {/* The triangle tip for the speech bubble */}
                    <AnimatePresence mode="wait">{isPlaying && 
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.4, ease: "easeInOut" }}
                            className="w-3 h-3 bg-white rounded-full absolute top-1/2 -translate-y-1/2 -translate-x-1/3"></motion.div>}
                    </AnimatePresence>

                </div>

            </div>

        </div>
    );
}